/**
 * State machine for the app layout's left nav: whether the docked nav is
 * expanded, and how the floating (hover) panel opens, closes, and hands off to
 * the docked nav. The reducer is pure; the layout component owns the timers
 * that fire the `*Elapsed`/`*Completed` actions.
 */

export type FloatingNavPhase =
  // Hover intent registered; waiting out the open delay.
  | "opening"
  | "open"
  // Hover left; waiting out the close delay before hiding.
  | "closing"
  | "closed"
  // Animating out before the docked nav expands.
  | "handingOff";

export interface NavLayoutState {
  /** The user's persisted preference. */
  prefersCollapsedNav: boolean;
  /** Number of mounted descendants requesting a collapsed nav (e.g. editor pages). */
  temporaryCollapseRequestCount: number;
  /** The user expanded the nav despite temporary collapse requests; reset when the last request is released. */
  isTemporaryExpansionRequested: boolean;
  isToggleHovered: boolean;
  isFloatingNavHovered: boolean;
  /** The floating nav's part of the document is inert (a dropdown or modal is open), so it should stay open. */
  hasFloatingNavInertParent: boolean;
  floatingNavPhase: FloatingNavPhase;
}

export type NavLayoutAction =
  | { type: "toggleClicked" }
  | { type: "toggleHoverChanged"; isHovered: boolean }
  | { type: "floatingNavHoverChanged"; isHovered: boolean }
  | { type: "floatingNavInertChanged"; hasInertParent: boolean }
  | { type: "temporaryCollapseRequested" }
  | { type: "temporaryCollapseReleased" }
  | { type: "openDelayElapsed" }
  | { type: "closeDelayElapsed" }
  | { type: "handoffAnimationCompleted" };

export function createInitialNavLayoutState(options: {
  prefersCollapsedNav: boolean;
}): NavLayoutState {
  return {
    prefersCollapsedNav: options.prefersCollapsedNav,
    temporaryCollapseRequestCount: 0,
    isTemporaryExpansionRequested: false,
    isToggleHovered: false,
    isFloatingNavHovered: false,
    hasFloatingNavInertParent: false,
    floatingNavPhase: "closed",
  };
}

export function selectIsNavExpanded(state: NavLayoutState): boolean {
  if (state.temporaryCollapseRequestCount > 0) {
    return state.isTemporaryExpansionRequested;
  }
  return !state.prefersCollapsedNav;
}

/** Whether the floating panel shows its open styles (it animates out during the other phases). */
export function selectIsFloatingNavVisible(state: NavLayoutState): boolean {
  return (
    state.floatingNavPhase === "open" || state.floatingNavPhase === "closing"
  );
}

function expandNav(state: NavLayoutState): NavLayoutState {
  if (state.temporaryCollapseRequestCount > 0) {
    return { ...state, isTemporaryExpansionRequested: true };
  }
  return { ...state, prefersCollapsedNav: false };
}

function collapseNav(state: NavLayoutState): NavLayoutState {
  if (state.temporaryCollapseRequestCount > 0) {
    return { ...state, isTemporaryExpansionRequested: false };
  }
  return { ...state, prefersCollapsedNav: true };
}

/** Re-derives the floating nav phase after any of its inputs change. */
function settleFloatingNavPhase(state: NavLayoutState): NavLayoutState {
  // A handoff runs to completion regardless of hover changes.
  if (state.floatingNavPhase === "handingOff") {
    return state;
  }

  if (selectIsNavExpanded(state)) {
    if (state.floatingNavPhase === "closed" && !state.isFloatingNavHovered) {
      return state;
    }
    // Also clear the panel hover: hiding the panel from under the pointer does
    // not reliably fire a mouse leave, and a stale hover would pop the panel
    // back open the next time the nav collapses.
    return {
      ...state,
      floatingNavPhase: "closed",
      isFloatingNavHovered: false,
    };
  }

  const shouldBeOpen =
    state.isToggleHovered ||
    state.isFloatingNavHovered ||
    state.hasFloatingNavInertParent;

  if (shouldBeOpen) {
    if (state.floatingNavPhase === "closed") {
      return { ...state, floatingNavPhase: "opening" };
    }
    if (state.floatingNavPhase === "closing") {
      return { ...state, floatingNavPhase: "open" };
    }
    return state;
  }

  if (state.floatingNavPhase === "opening") {
    return { ...state, floatingNavPhase: "closed" };
  }
  if (state.floatingNavPhase === "open") {
    return { ...state, floatingNavPhase: "closing" };
  }
  return state;
}

function reduceToggleClicked(state: NavLayoutState): NavLayoutState {
  // The expand intent is already in flight; acting on further clicks would
  // expand the docked nav while the floating panel is still animating out.
  if (state.floatingNavPhase === "handingOff") {
    return state;
  }
  if (selectIsNavExpanded(state)) {
    return settleFloatingNavPhase(collapseNav(state));
  }
  // Animate the floating panel out before expanding the docked nav so the two
  // panels don't overlap mid-transition.
  if (selectIsFloatingNavVisible(state)) {
    return { ...state, floatingNavPhase: "handingOff" };
  }
  return settleFloatingNavPhase(expandNav(state));
}

export function reduceNavLayoutState(
  state: NavLayoutState,
  action: NavLayoutAction,
): NavLayoutState {
  switch (action.type) {
    case "toggleClicked":
      return reduceToggleClicked(state);
    case "toggleHoverChanged":
      if (state.isToggleHovered === action.isHovered) {
        return state;
      }
      return settleFloatingNavPhase({
        ...state,
        isToggleHovered: action.isHovered,
      });
    case "floatingNavHoverChanged":
      if (state.isFloatingNavHovered === action.isHovered) {
        return state;
      }
      return settleFloatingNavPhase({
        ...state,
        isFloatingNavHovered: action.isHovered,
      });
    case "floatingNavInertChanged":
      if (state.hasFloatingNavInertParent === action.hasInertParent) {
        return state;
      }
      return settleFloatingNavPhase({
        ...state,
        hasFloatingNavInertParent: action.hasInertParent,
      });
    case "temporaryCollapseRequested":
      return settleFloatingNavPhase({
        ...state,
        temporaryCollapseRequestCount: state.temporaryCollapseRequestCount + 1,
      });
    case "temporaryCollapseReleased": {
      const temporaryCollapseRequestCount = Math.max(
        0,
        state.temporaryCollapseRequestCount - 1,
      );
      return settleFloatingNavPhase({
        ...state,
        temporaryCollapseRequestCount,
        isTemporaryExpansionRequested:
          temporaryCollapseRequestCount > 0 &&
          state.isTemporaryExpansionRequested,
      });
    }
    case "openDelayElapsed":
      return state.floatingNavPhase === "opening"
        ? { ...state, floatingNavPhase: "open" }
        : state;
    case "closeDelayElapsed":
      return state.floatingNavPhase === "closing"
        ? { ...state, floatingNavPhase: "closed" }
        : state;
    case "handoffAnimationCompleted":
      if (state.floatingNavPhase !== "handingOff") {
        return state;
      }
      return settleFloatingNavPhase(
        expandNav({ ...state, floatingNavPhase: "closed" }),
      );
  }
}
